import { useEffect, useRef, useState } from 'react';
import { rememberReferencePoint } from '../../utils/geo';
import { FIX_MAX_AGE_MS, freshFix } from '../../utils/fieldBatches';

export const WATCH_OPTIONS = { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 };

/**
 * Where the phone is, followed while `active` (the camera screen is open).
 *
 * Answers {fix, fixRef, state}. A fix is {lat, lng, accuracy_m, at}: six
 * decimals and a tenth of a metre, what the photo columns hold. `fixRef`
 * always holds the latest, for a handler that must not wait for a render.
 * `state` is 'ok' while the latest fix is under two minutes old, 'locating'
 * until one comes, and 'none' when the device said it cannot -- which it may
 * take back with a fix later, so the watch is kept.
 */
export function useGeoWatch(active = true) {
  const [fix, setFix] = useState(null);
  const [failed, setFailed] = useState(false);
  const [, setClock] = useState(0);
  const fixRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const geo = typeof navigator !== 'undefined' ? navigator.geolocation : null;
    if (!geo || typeof geo.watchPosition !== 'function') {
      setFailed(true);
      return undefined;
    }
    const id = geo.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const next = {
          lat: Number(Number(latitude).toFixed(6)),
          lng: Number(Number(longitude).toFixed(6)),
          accuracy_m: Number.isFinite(accuracy) ? Math.round(accuracy * 10) / 10 : null,
          at: Date.now(),
        };
        // The device said where it is: what MapGpsPicker's jamming check
        // compares a later position with.
        rememberReferencePoint(next.lat, next.lng);
        fixRef.current = next;
        setFix(next);
        setFailed(false);
      },
      () => setFailed(true),
      WATCH_OPTIONS,
    );
    // A phone standing still reports nothing new, so whether the last fix is
    // still fresh is looked at again now and then.
    const tick = setInterval(() => setClock(c => c + 1), 30 * 1000);
    return () => {
      geo.clearWatch(id);
      clearInterval(tick);
    };
  }, [active]);

  const fresh = freshFix(fix, Date.now(), FIX_MAX_AGE_MS);
  let state = 'locating';
  if (fresh) state = 'ok';
  else if (failed) state = 'none';
  return { fix: fresh, fixRef, state };
}
